import {Action, combineReducers, configureStore, ThunkAction} from '@reduxjs/toolkit';
import {
    persistStore,
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import PlaylistSlice from "./slices/playlist/redcuers";
import PlayerSlice from "./slices/player/reducers";
import HomeSlice from "./slices/home/reducers";
import SearchSlice from "./slices/search/reducers";
import UserSlice from "./slices/user/reducers";
import UserPlaylistsSlice from "./slices/userPlaylists/reducers";
import FavoritesSlice from "./slices/favorites/reducers";
import LikedSongsSlice from "./slices/likedSongs/reducers";
import SettingsSlice from "./slices/settings/reducers";
import CastSlice from "./slices/cast/reducers";
import {playlistsSyncMiddleware} from "./sync/playlistsSyncMiddleware";
import {userDataSyncMiddleware} from "./sync/userDataSyncMiddleware";
import {useDispatch} from "react-redux";

const playerPersistConfig = {
    key: 'player',
    storage,
    blacklist: ['status', 'isInitiatedByPlayer'], // Prevent autoplay on a mobile load
};

const persistConfig = {
    key: 'root',
    // 'player' is intentionally absent — its inner persistReducer owns
    // persistence under `persist:player`. Listing it here too would
    // double-persist the slice and silently bypass the inner blacklist.
    storage,
    whitelist: ['playlist', 'user', 'userPlaylists', 'favorites', 'likedSongs', 'settings'],
};

const rootReducer = combineReducers({
    home: HomeSlice,
    player: persistReducer(playerPersistConfig, PlayerSlice),
    playlist: PlaylistSlice,
    search: SearchSlice,
    user: UserSlice,
    userPlaylists: UserPlaylistsSlice,
    favorites: FavoritesSlice,
    likedSongs: LikedSongsSlice,
    settings: SettingsSlice,
    cast: CastSlice,
});
const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }).prepend(playlistsSyncMiddleware.middleware, userDataSyncMiddleware.middleware)
});

export const persistor = persistStore(store);

export default store;

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export type RootState = ReturnType<typeof store.getState>
export type AppThunk<ReturnType = any> = ThunkAction<ReturnType, RootState, unknown, Action<string>>
